import SafeSummaryTile from "../../../components/common/SafeSummaryTile";
import SafeMobileRecordList from "../../../components/common/SafeMobileRecordList";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GroupsIcon from "@mui/icons-material/Groups";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShieldIcon from "@mui/icons-material/Shield";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { toast } from "react-toastify";

import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import {
  EmployeeBulkPicker,
  EmployeeSelect,
} from "../../../components/common/EmployeeSelect";
import { SafePageHeader } from "../../../components/common/HelperFunc";
import { getCertificateStatus, hasCertificateExpiry } from "../../../utils/certificateStatus";

const tabs = [
  {
    value: "legalCerts",
    label: "설비·구역 검사·인증 관리",
    icon: <VerifiedUserIcon fontSize="small" />,
    emptyText: "등록된 설비·구역 검사·인증 관리 자료가 없습니다.",
  },
  {
    value: "committees",
    label: "안전보건위원회",
    icon: <GroupsIcon fontSize="small" />,
    emptyText: "등록된 안전보건위원회가 없습니다.",
  },
];

const initialData = {
  msdsTrainings: [],
  msdsGuides: [],
  eduMaterials: [],
  legalCerts: [],
  committees: [],
  committeeMeetings: [],
};

const initialSelected = {
  msdsTrainings: [],
  msdsGuides: [],
  eduMaterials: [],
  legalCerts: [],
  committees: [],
  committeeMeetings: [],
};

const defaultOptions = {
  msds: [],
  machines: [],
  processes: [],
  eduSchedules: [],
  employees: [],
};

const statusLabels = {
  certResult: {
    0: { label: "적합", color: "success" },
    1: { label: "조건부", color: "warning" },
    2: { label: "부적합", color: "error" },
  },
  certStatus: {
    0: { label: "유효", color: "success" },
    1: { label: "만료 임박", color: "warning" },
    2: { label: "만료", color: "error" },
    3: { label: "폐기", color: "default" },
  },
  meetingStatus: {
    0: { label: "예정", color: "warning" },
    1: { label: "완료", color: "success" },
    2: { label: "취소", color: "default" },
  },
};

const emptyForms = {
  msdsTrainings: {
    msds_id: "",
    machine_id: "",
    process_id: "",
    title: "",
    trainer_emp_id: "",
    training_at: "",
    duration_min: "",
    target_desc: "",
    note: "",
    attendee_ids: [],
  },
  msdsGuides: {
    msds_id: "",
    process_id: "",
    machine_id: "",
    guide_title: "",
    guide_summary: "",
    revision: 1,
    effective_from: "",
    expire_at: "",
    created_by_emp_id: "",
    pdf: null,
  },
  eduMaterials: {
    schedule_id: "",
    title: "",
    revision: 1,
    is_primary: 0,
    uploaded_by_emp_id: "",
    pdf: null,
  },
  legalCerts: {
    cert_type: "안전검사",
    cert_name: "",
    certificate_no: "",
    agency: "",
    inspected_at: "",
    issued_at: "",
    expire_at: "",
    result_status: 0,
    status: 0,
    memo: "",
    machine_ids: [],
    pdf: null,
  },
  committees: {
    committee_name: "",
    description: "",
    member_ids: [],
  },
  committeeMeetings: {
    committee_id: "",
    meeting_title: "",
    meeting_at: "",
    meeting_location: "",
    agenda: "",
    decision_summary: "",
    status: 0,
    created_by_emp_id: "",
    attendee_ids: [],
    minutes_pdf: null,
    photo: null,
  },
};

const idFields = {
  msdsTrainings: "session_id",
  msdsGuides: "guide_id",
  eduMaterials: "material_id",
  legalCerts: "cert_id",
  committees: "committee_id",
  committeeMeetings: "meeting_id",
};

const endpointMap = {
  msdsTrainings: "msds-trainings",
  msdsGuides: "msds-guides",
  eduMaterials: "edu-materials",
  legalCerts: "legal-certs",
  committees: "committees",
  committeeMeetings: "committee-meetings",
};

const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");
const toDateTimeInput = (value) =>
  value ? String(value).replace(" ", "T").slice(0, 16) : "";

const listFromIds = (options, ids, idKey) => {
  const set = new Set((ids || []).map(Number));
  return options.filter((option) => set.has(Number(option[idKey])));
};

const employeeLabel = (employee) => {
  if (!employee) return "";
  const no = employee.emp_no ? ` (${employee.emp_no})` : "";
  const team = employee.team_name || employee.department_name || "";
  return `${employee.emp_name || employee.user_name || employee.emp_id}${no}${
    team ? ` · ${team}` : ""
  }`;
};

const machineLabel = (machine) =>
  machine?.display_name ||
  machine?.safe_machine_name ||
  machine?.machine_name ||
  machine?.machine_manage_code ||
  (machine?.machine_id ? `설비·구역 ${machine.machine_id}` : "");

const msdsLabel = (msds) =>
  msds?.product_name ? `${msds.product_name}${msds.manufacturer ? ` · ${msds.manufacturer}` : ""}` : "";

const processLabel = (process) =>
  process?.process_name ||
  (process?.process_id ? `평가 대상 ${process.process_id}` : "");

const scheduleLabel = (schedule) =>
  schedule?.title
    ? `${schedule.title}${schedule.edu_date ? ` · ${String(schedule.edu_date).slice(0, 10)}` : ""}`
    : "";

const buildFormData = (form, fileFields = []) => {
  const data = new FormData();
  Object.entries(form).forEach(([key, value]) => {
    if (fileFields.includes(key)) {
      if (value) data.append(key, value);
      return;
    }
    if (Array.isArray(value)) {
      data.append(key, JSON.stringify(value));
      return;
    }
    data.append(key, value ?? "");
  });
  return data;
};

const fileUrl = (url) => {
  if (!url) return "";
  const baseUrl = axiosInstance.defaults.baseURL || "";
  return `${String(baseUrl).replace(/\/$/, "")}${url}`;
};

const StatCard = (props) => <SafeSummaryTile {...props} />;

const FileButton = ({ url, label = "열기" }) =>
  url ? (
    <Button
      size="small"
      variant="text"
      endIcon={<OpenInNewIcon fontSize="small" />}
      onClick={() => window.open(fileUrl(url), "_blank", "noopener,noreferrer")}
      sx={{ minWidth: 0, fontWeight: 700, px: 0.5 }}
    >
      {label}
    </Button>
  ) : (
    <Chip size="small" variant="outlined" label="없음" sx={{ color: "text.secondary" }} />
  );

const StatusChip = ({ map, value }) => {
  const item = map[value] || { label: "-", color: "default" };
  return <Chip size="small" label={item.label} color={item.color} sx={{ fontWeight: 800 }} />;
};

const CompactTable = ({
  rows,
  columns,
  idField,
  selected,
  onSelect,
  onEdit,
  emptyText,
  compact = false,
}) => {
  const allSelected = rows.length > 0 && selected.length === rows.length;
  const compactScreen = useMediaQuery(useTheme().breakpoints.down("lg"));

  if (compactScreen) return <SafeMobileRecordList rows={rows} columns={columns} titleKey="cert_name" idField={idField}
    selected={selected} onSelect={onSelect} onEdit={onEdit} emptyText={emptyText} />;

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        overflow: "auto",
        maxHeight: { xs: "none", md: compact ? 360 : 520 },
        bgcolor: "#fff",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: compact ? 760 : 880 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" align="center">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={selected.length > 0 && selected.length < rows.length}
                onChange={(event) =>
                  onSelect(event.target.checked ? rows.map((row) => row[idField]) : [])
                }
              />
            </TableCell>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align || "center"}
                sx={{
                  minWidth: column.wrap ? 180 : undefined,
                  fontWeight: 900,
                  bgcolor: "#f7f9fc",
                  whiteSpace: "nowrap",
                  textAlign: column.align || "center",
                  py: 1.2,
                }}
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 900, bgcolor: "#f7f9fc", py: 1.2 }}>
              수정
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!rows.length ? (
            <TableRow>
              <TableCell colSpan={columns.length + 2} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary" sx={{ fontWeight: 800 }}>
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const rowId = row[idField];
              return (
                <TableRow
                  key={rowId}
                  hover
                  sx={{
                    "&:last-of-type td": { borderBottom: 0 },
                    "&:hover td": { bgcolor: "#fbfdff" },
                  }}
                >
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      size="small"
                      checked={selected.includes(rowId)}
                      onChange={(event) => {
                        if (event.target.checked) onSelect([...selected, rowId]);
                        else onSelect(selected.filter((id) => id !== rowId));
                      }}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "center"}
                      sx={{
                        minWidth: column.wrap ? 180 : undefined,
                        maxWidth: column.maxWidth || 260,
                        whiteSpace: column.wrap ? "normal" : "nowrap",
                        wordBreak: "keep-all",
                        overflowWrap: "anywhere",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        py: compact ? 1.15 : 1.3,
                        verticalAlign: "middle",
                      }}
                    >
                      {column.render ? column.render(row) : row[column.key] || "-"}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Tooltip title="수정">
                      <IconButton size="small" onClick={() => onEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SectionHeader = ({ title, icon, description, children, compact = false }) => (
  <Stack
    className="safe-section-heading"
    direction={{ xs: "column", md: "row" }}
    alignItems={{ xs: "stretch", md: "center" }}
    justifyContent="space-between"
    gap={1.5}
    sx={{ mb: compact ? 1.1 : 1.5 }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.2,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(99, 102, 241, 0.1)",
          color: "primary.main",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography component="h2" className="safe-section-title" sx={{ fontSize: compact ? 16 : 18, fontWeight: 900, lineHeight: 1.25 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
    </Stack>
    <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
      {children}
    </Stack>
  </Stack>
);

const SelectField = ({ label, value, onChange, options, getValue, getLabel, required, disabled }) => (
  <FormControl fullWidth size="small" required={required} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
      <MenuItem value="">선택 안 함</MenuItem>
      {options.map((option) => (
        <MenuItem key={getValue(option)} value={getValue(option)}>
          {getLabel(option)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const LegalComplianceManagement = ({
  visibleTabs,
  title,
  description,
  hideSummary = false,
  embedded = false,
}) => {
  const { group_code: groupCode, project_code: projectCode } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const filteredTabs = useMemo(
    () => (visibleTabs?.length ? tabs.filter((tab) => visibleTabs.includes(tab.value)) : tabs),
    [visibleTabs]
  );
  const [activeTab, setActiveTab] = useState(filteredTabs[0]?.value || "msdsTrainings");
  const [legalCertStatusFilter, setLegalCertStatusFilter] = useState("all");
  const [options, setOptions] = useState(defaultOptions);
  const [data, setData] = useState(initialData);
  const [selected, setSelected] = useState(initialSelected);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({ open: false, type: "", row: null });
  const [form, setForm] = useState(emptyForms.msdsTrainings);

  const baseUrl = useMemo(
    () => `/risk/${groupCode}/${projectCode}/compliance`,
    [groupCode, projectCode]
  );

  useEffect(() => {
    if (!filteredTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(filteredTabs[0]?.value || "msdsTrainings");
    }
  }, [activeTab, filteredTabs]);

  const activeMeta = filteredTabs.find((tab) => tab.value === activeTab) || filteredTabs[0] || tabs[0];

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        optionRes,
        trainingRes,
        guideRes,
        materialRes,
        certRes,
        committeeRes,
        meetingRes,
      ] = await Promise.all([
        axiosInstance.get(`${baseUrl}/options`),
        axiosInstance.get(`${baseUrl}/msds-trainings`),
        axiosInstance.get(`${baseUrl}/msds-guides`),
        axiosInstance.get(`${baseUrl}/edu-materials`),
        axiosInstance.get(`${baseUrl}/legal-certs`),
        axiosInstance.get(`${baseUrl}/committees`),
        axiosInstance.get(`${baseUrl}/committee-meetings`),
      ]);

      setOptions({ ...defaultOptions, ...optionRes.data });
      setData({
        msdsTrainings: trainingRes.data || [],
        msdsGuides: guideRes.data || [],
        eduMaterials: materialRes.data || [],
        legalCerts: certRes.data || [],
        committees: committeeRes.data || [],
        committeeMeetings: meetingRes.data || [],
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "법정 관리 데이터를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupCode && projectCode) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  const employeeMap = useMemo(() => {
    const map = new Map();
    options.employees.forEach((employee) => map.set(Number(employee.emp_id), employee));
    return map;
  }, [options.employees]);

  const openDialog = (type, row = null) => {
    const next = {
      ...emptyForms[type],
      ...(row || {}),
    };
    for (const [key, defaultValue] of Object.entries(emptyForms[type])) {
      if (typeof defaultValue === "string" && next[key] == null) next[key] = defaultValue;
    }

    if (type === "msdsTrainings") {
      next.training_at = toDateTimeInput(row?.training_at);
      next.attendee_ids = row?.attendee_ids || [];
    }
    if (type === "msdsGuides") {
      next.effective_from = toDateInput(row?.effective_from);
      next.expire_at = toDateInput(row?.expire_at);
      next.pdf = null;
    }
    if (type === "eduMaterials") {
      next.pdf = null;
    }
    if (type === "legalCerts") {
      next.inspected_at = toDateInput(row?.inspected_at);
      next.issued_at = toDateInput(row?.issued_at);
      next.expire_at = toDateInput(row?.expire_at);
      next.machine_ids = row?.machine_ids || [];
      next.pdf = null;
    }
    if (type === "committees") {
      next.member_ids = row?.member_ids || [];
    }
    if (type === "committeeMeetings") {
      next.meeting_at = toDateTimeInput(row?.meeting_at);
      next.attendee_ids = row?.attendee_ids || [];
      next.minutes_pdf = null;
      next.photo = null;
    }

    setForm(next);
    setDialog({ open: true, type, row });
  };

  const closeDialog = () => {
    setDialog({ open: false, type: "", row: null });
    setForm(emptyForms.msdsTrainings);
  };

  const submitDialog = async () => {
    const type = dialog.type;
    const endpoint = endpointMap[type];
    const id = dialog.row?.[idFields[type]];
    const url = `${baseUrl}/${endpoint}${id ? `/${id}` : ""}`;

    setSaving(true);
    try {
      let payload = form;
      let config = {};
      if (["msdsGuides", "eduMaterials", "legalCerts"].includes(type)) {
        payload = buildFormData(type === "legalCerts" ? { ...form, status: getCertificateStatus(form) } : form, ["pdf"]);
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }
      if (type === "committeeMeetings") {
        payload = buildFormData(form, ["minutes_pdf", "photo"]);
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }

      if (id) await axiosInstance.put(url, payload, config);
      else await axiosInstance.post(url, payload, config);

      toast.success(id ? "수정되었습니다." : "등록되었습니다.");
      closeDialog();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRows = async (type = activeTab) => {
    const ids = selected[type] || [];
    if (!ids.length) {
      toast.info("삭제할 항목을 선택해주세요.");
      return;
    }

    if (!window.confirm(`${ids.length}건을 삭제하시겠습니까?`)) return;

    try {
      await axiosInstance.delete(`${baseUrl}/${endpointMap[type]}`, { data: { ids } });
      setSelected((prev) => ({ ...prev, [type]: [] }));
      toast.success("삭제되었습니다.");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  const summary = useMemo(
    () => ({
      trainings: data.msdsTrainings.length,
      guides: data.msdsGuides.length,
      materials: data.eduMaterials.length,
      certs: data.legalCerts.length,
      committees: data.committees.length,
      meetings: data.committeeMeetings.length,
      validCerts: data.legalCerts.filter((item) => getCertificateStatus(item) === 0).length,
      expiringCerts: data.legalCerts.filter((item) => getCertificateStatus(item) === 1).length,
      expiredCerts: data.legalCerts.filter((item) => getCertificateStatus(item) === 2).length,
      completedMeetings: data.committeeMeetings.filter((item) => Number(item.status) === 1).length,
      plannedMeetings: data.committeeMeetings.filter((item) => Number(item.status) === 0).length,
    }),
    [data]
  );

  const summaryCards = useMemo(
    () => {
      const rawCards = [
        {
          key: "legalCerts",
          filter: "all",
          icon: <VerifiedUserIcon fontSize="small" />,
          label: "전체 검사·인증",
          value: `${summary.certs}건`,
          color: "warning",
        },
        {
          key: "legalCerts",
          filter: "0",
          icon: <ShieldIcon fontSize="small" />,
          label: "유효",
          value: `${summary.validCerts}건`,
          color: "success",
        },
        {
          key: "legalCerts",
          filter: "1",
          icon: <FactCheckIcon fontSize="small" />,
          label: "만료 임박",
          value: `${summary.expiringCerts}건`,
          color: "error",
        },
        {
          key: "legalCerts",
          filter: "2",
          icon: <FactCheckIcon fontSize="small" />,
          label: "만료",
          value: `${summary.expiredCerts}건`,
          color: "error",
        },
        {
          key: "committees",
          icon: <GroupsIcon fontSize="small" />,
          label: "위원회",
          value: `${summary.committees}건`,
          color: "secondary",
        },
        {
          key: "committeeMeetings",
          icon: <FactCheckIcon fontSize="small" />,
          label: "회의록",
          value: `${summary.meetings}건`,
          color: "primary",
        },
        {
          key: "committeeMeetings",
          icon: <FactCheckIcon fontSize="small" />,
          label: "완료 회의",
          value: `${summary.completedMeetings}건`,
          color: "success",
        },
        {
          key: "committeeMeetings",
          icon: <FactCheckIcon fontSize="small" />,
          label: "예정 회의",
          value: `${summary.plannedMeetings}건`,
          color: "warning",
        },
      ];

      return rawCards.filter((card) => !visibleTabs?.length || visibleTabs.includes(card.key));
    },
    [summary, visibleTabs]
  );
  const summaryGridMd = summaryCards.length
    ? Math.max(2, 12 / Math.min(summaryCards.length, 4))
    : 3;

  const visibleLegalCerts = useMemo(() => {
    if (legalCertStatusFilter === "all") return data.legalCerts;
    return data.legalCerts.filter((item) => String(getCertificateStatus(item)) === legalCertStatusFilter);
  }, [data.legalCerts, legalCertStatusFilter]);

  const columns = {
    msdsTrainings: [
      { key: "title", label: "교육명", align: "left", wrap: true },
      {
        key: "msds_name",
        label: "MSDS",
        align: "left",
        render: (row) => row.msds_name || "-",
      },
      {
        key: "target",
        label: "대상",
        render: (row) => (
          <Stack spacing={0.3} alignItems="center">
            <Typography variant="body2">{row.target_desc || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.process_name || row.safe_machine_name || row.machine_name || ""}
            </Typography>
          </Stack>
        ),
      },
      { key: "training_at", label: "교육 일시" },
      {
        key: "trainer_emp_id",
        label: "강사",
        render: (row) => employeeLabel(employeeMap.get(Number(row.trainer_emp_id))) || "-",
      },
      {
        key: "attendees",
        label: "이수",
        render: (row) => `${row.completed_count || 0}/${row.attendee_count || 0}명`,
      },
    ],
    msdsGuides: [
      { key: "guide_title", label: "관리요령", align: "left", wrap: true },
      { key: "msds_name", label: "MSDS", align: "left" },
      { key: "process_name", label: "평가 대상" },
      {
        key: "machine_name",
        label: "설비·구역",
        render: (row) => row.safe_machine_name || row.machine_name || "-",
      },
      { key: "revision", label: "개정" },
      { key: "expire_at", label: "만료일" },
      { key: "pdf_url", label: "PDF", render: (row) => <FileButton url={row.pdf_url} /> },
    ],
    eduMaterials: [
      { key: "title", label: "자료명", align: "left", wrap: true },
      { key: "schedule_title", label: "교육 일정", align: "left", wrap: true },
      { key: "edu_type", label: "교육 유형" },
      { key: "edu_date", label: "교육 일시" },
      { key: "revision", label: "개정" },
      {
        key: "is_primary",
        label: "대표",
        render: (row) =>
          Number(row.is_primary) ? (
            <Chip size="small" color="primary" label="대표" sx={{ fontWeight: 800 }} />
          ) : (
            "-"
          ),
      },
      { key: "pdf_url", label: "PDF", render: (row) => <FileButton url={row.pdf_url} /> },
    ],
    legalCerts: [
      { key: "cert_type", label: "구분" },
      { key: "cert_name", label: "검사·인증명", align: "left", wrap: true },
      { key: "certificate_no", label: "증서번호" },
      { key: 'location_names', label: '라인', align: 'left', wrap: true,
        render: (row) => [...new Set((row.machines || []).map(machine => machine.location_names || '라인 미지정'))].join(' / ') || '라인 미지정' },
      {
        key: "machines",
        label: "대상 설비·구역",
        render: (row) =>
          row.machines?.length ? (
            <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="center">
              {row.machines.slice(0, 3).map((machine) => (
                <Tooltip key={machine.machine_id} title={machine.location_names || '라인 미지정'}><Chip size="small" label={machineLabel(machine)} /></Tooltip>
              ))}
              {row.machines.length > 3 && <Chip size="small" label={`+${row.machines.length - 3}`} />}
            </Stack>
          ) : (
            "-"
          ),
      },
      { key: "expire_at", label: "유효기간" },
      {
        key: "result_status",
        label: "결과",
        render: (row) => <StatusChip map={statusLabels.certResult} value={Number(row.result_status)} />,
      },
      {
        key: "status",
        label: "상태",
        render: (row) => <StatusChip map={statusLabels.certStatus} value={getCertificateStatus(row)} />,
      },
      { key: "pdf_url", label: "PDF", render: (row) => <FileButton url={row.pdf_url} /> },
    ],
    committees: [
      { key: "committee_name", label: "위원회", align: "left", wrap: true },
      { key: "description", label: "설명", align: "left", wrap: true },
      { key: "member_count", label: "위원" },
      { key: "updated_at", label: "최근 수정" },
    ],
    committeeMeetings: [
      { key: "meeting_title", label: "회의명", align: "left", wrap: true },
      { key: "committee_name", label: "위원회", align: "left" },
      { key: "meeting_at", label: "회의 일시" },
      { key: "meeting_location", label: "장소" },
      {
        key: "status",
        label: "상태",
        render: (row) => <StatusChip map={statusLabels.meetingStatus} value={Number(row.status)} />,
      },
      {
        key: "attendees",
        label: "참석",
        render: (row) => `${row.attended_count || 0}/${row.attendee_count || 0}명`,
      },
      {
        key: "files",
        label: "첨부",
        render: (row) => (
          <Stack direction="row" gap={0.5} justifyContent="center">
            <FileButton url={row.minutes_pdf_url} label="회의록" />
            <FileButton url={row.photo_url} label="사진" />
          </Stack>
        ),
      },
    ],
  };

  const renderEmployeeMultiSelect = (label, key) => {
    const currentIds = (form[key] || []).map(Number);
    const currentSet = new Set(currentIds);

    const selectEmployee = (employee, checked) => {
      const empId = Number(employee.emp_id);
      if (!empId) return;
      if (checked) {
        setField(key, Array.from(new Set([...currentIds, empId])));
        return;
      }
      setField(
        key,
        currentIds.filter((id) => id !== empId)
      );
    };

    const selectEmployees = (employees) => {
      const next = new Set(currentIds);
      employees.forEach((employee) => {
        const empId = Number(employee.emp_id);
        if (empId) next.add(empId);
      });
      setField(key, Array.from(next));
    };

    const deselectEmployees = (employees) => {
      const removeSet = new Set(employees.map((employee) => Number(employee.emp_id)));
      setField(
        key,
        currentIds.filter((id) => !removeSet.has(id))
      );
    };

    return (
      <EmployeeBulkPicker
        employees={options.employees}
        selectedIds={Array.from(currentSet)}
        title={label}
        selectedLabel="선택"
        helperText="팀 전체를 한 번에 넣거나 이름·사번으로 검색해서 일부만 선택할 수 있습니다."
        listHeight={{ xs: 280, md: 360 }}
        fixedListHeight
        onSelectEmployee={selectEmployee}
        onSelectEmployees={selectEmployees}
        onDeselectEmployees={deselectEmployees}
      />
    );
  };

  const renderEmployeeSelect = (label, key) => (
    <EmployeeSelect
      employees={options.employees}
      value={form[key] || ""}
      onChange={(value) => setField(key, value)}
      label={label}
      helperText="팀을 먼저 선택하면 근로자를 빠르게 찾을 수 있습니다."
    />
  );

  const renderMachineMultiSelect = (label, key) => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options.machines}
      value={listFromIds(options.machines, form[key], "machine_id")}
      getOptionLabel={machineLabel}
      isOptionEqualToValue={(option, value) => Number(option.machine_id) === Number(value.machine_id)}
      onChange={(event, values) => setField(key, values.map((machine) => machine.machine_id))}
      renderInput={(params) => <TextField {...params} label={label} size="small" />}
    />
  );

  const renderFileInput = (key, label, accept = "application/pdf") => (
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
      <Button
        variant="outlined"
        component="label"
        startIcon={<PictureAsPdfIcon />}
        sx={{ fontWeight: 900 }}
      >
        {label}
        <input
          hidden
          type="file"
          accept={accept}
          onChange={(event) => setField(key, event.target.files?.[0] || null)}
        />
      </Button>
      <Typography variant="body2" color="text.secondary" noWrap>
        {form[key]?.name || "선택된 파일 없음"}
      </Typography>
    </Stack>
  );

  const renderDialogContent = () => {
    const type = dialog.type;

    if (type === "msdsTrainings") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="MSDS"
              value={form.msds_id}
              onChange={(value) => setField("msds_id", value)}
              options={options.msds}
              getValue={(item) => item.id}
              getLabel={msdsLabel}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="교육명"
              value={form.title}
              onChange={(event) => setField("title", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectField
              label="사용 설비·구역"
              value={form.machine_id}
              onChange={(value) => setField("machine_id", value)}
              options={options.machines}
              getValue={(item) => item.machine_id}
              getLabel={machineLabel}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectField
              label="평가 대상"
              value={form.process_id}
              onChange={(value) => setField("process_id", value)}
              options={options.processes}
              getValue={(item) => item.process_id}
              getLabel={processLabel}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="교육 일시"
              type="datetime-local"
              value={form.training_at}
              onChange={(event) => setField("training_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="교육 시간(분)"
              type="number"
              value={form.duration_min}
              onChange={(event) => setField("duration_min", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {renderEmployeeSelect("강사", "trainer_emp_id")}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="대상 설명"
              value={form.target_desc}
              onChange={(event) => setField("target_desc", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {renderEmployeeMultiSelect("교육 참석자", "attendee_ids")}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="비고"
              value={form.note}
              onChange={(event) => setField("note", event.target.value)}
            />
          </Grid>
        </Grid>
      );
    }

    if (type === "msdsGuides") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="MSDS"
              value={form.msds_id}
              onChange={(value) => setField("msds_id", value)}
              options={options.msds}
              getValue={(item) => item.id}
              getLabel={msdsLabel}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="관리요령 제목"
              value={form.guide_title}
              onChange={(event) => setField("guide_title", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectField
              label="평가 대상"
              value={form.process_id}
              onChange={(value) => setField("process_id", value)}
              options={options.processes}
              getValue={(item) => item.process_id}
              getLabel={processLabel}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectField
              label="설비·구역"
              value={form.machine_id}
              onChange={(value) => setField("machine_id", value)}
              options={options.machines}
              getValue={(item) => item.machine_id}
              getLabel={machineLabel}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="개정"
              type="number"
              value={form.revision}
              onChange={(event) => setField("revision", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="시행일"
              type="date"
              value={form.effective_from}
              onChange={(event) => setField("effective_from", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="만료일"
              type="date"
              value={form.expire_at}
              onChange={(event) => setField("expire_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="요약"
              value={form.guide_summary}
              onChange={(event) => setField("guide_summary", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {renderFileInput("pdf", "PDF 첨부")}
          </Grid>
        </Grid>
      );
    }

    if (type === "eduMaterials") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectField
              label="교육 일정"
              value={form.schedule_id}
              onChange={(value) => setField("schedule_id", value)}
              options={options.eduSchedules}
              getValue={(item) => item.id}
              getLabel={scheduleLabel}
              required
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="자료명"
              value={form.title}
              onChange={(event) => setField("title", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="개정"
              type="number"
              value={form.revision}
              onChange={(event) => setField("revision", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {renderEmployeeSelect("등록자", "uploaded_by_emp_id")}
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ height: "100%" }}>
              <Checkbox
                checked={Boolean(Number(form.is_primary))}
                onChange={(event) => setField("is_primary", event.target.checked ? 1 : 0)}
              />
              <Typography sx={{ fontWeight: 800 }}>대표 교육자료로 표시</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {renderFileInput("pdf", "PDF 첨부")}
          </Grid>
        </Grid>
      );
    }

    if (type === "legalCerts") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="구분"
              value={form.cert_type}
              onChange={(event) => setField("cert_type", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="검사·인증명"
              value={form.cert_name}
              onChange={(event) => setField("cert_name", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="증서번호"
              value={form.certificate_no}
              onChange={(event) => setField("certificate_no", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="기관"
              value={form.agency}
              onChange={(event) => setField("agency", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectField
              label={hasCertificateExpiry(form) ? "상태 (유효기간 기준)" : "상태"}
              value={getCertificateStatus(form)}
              disabled={hasCertificateExpiry(form)}
              onChange={(value) => setField("status", value)}
              options={[
                { value: 0, label: "유효" },
                { value: 1, label: "만료 임박" },
                { value: 2, label: "만료" },
              ]}
              getValue={(item) => item.value}
              getLabel={(item) => item.label}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="검사일"
              type="date"
              value={form.inspected_at}
              onChange={(event) => setField("inspected_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="발급일"
              type="date"
              value={form.issued_at}
              onChange={(event) => setField("issued_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="만료일"
              type="date"
              value={form.expire_at}
              onChange={(event) => setField("expire_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectField
              label="검사 결과"
              value={form.result_status}
              onChange={(value) => setField("result_status", value)}
              options={[
                { value: 0, label: "적합" },
                { value: 1, label: "조건부" },
                { value: 2, label: "부적합" },
              ]}
              getValue={(item) => item.value}
              getLabel={(item) => item.label}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {renderMachineMultiSelect("대상 설비·구역", "machine_ids")}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="비고"
              value={form.memo}
              onChange={(event) => setField("memo", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {renderFileInput("pdf", "PDF 첨부")}
          </Grid>
        </Grid>
      );
    }

    if (type === "committees") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="위원회명"
              value={form.committee_name}
              onChange={(event) => setField("committee_name", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="설명"
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {renderEmployeeMultiSelect("위원", "member_ids")}
          </Grid>
        </Grid>
      );
    }

    if (type === "committeeMeetings") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="위원회"
              value={form.committee_id}
              onChange={(value) => setField("committee_id", value)}
              options={data.committees}
              getValue={(item) => item.committee_id}
              getLabel={(item) => item.committee_name}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="회의명"
              value={form.meeting_title}
              onChange={(event) => setField("meeting_title", event.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="회의 일시"
              type="datetime-local"
              value={form.meeting_at}
              onChange={(event) => setField("meeting_at", event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="회의 장소"
              value={form.meeting_location}
              onChange={(event) => setField("meeting_location", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectField
              label="상태"
              value={form.status}
              onChange={(value) => setField("status", value)}
              options={[
                { value: 0, label: "예정" },
                { value: 1, label: "완료" },
                { value: 2, label: "취소" },
              ]}
              getValue={(item) => item.value}
              getLabel={(item) => item.label}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {renderEmployeeSelect("작성자", "created_by_emp_id")}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderEmployeeMultiSelect("참석자", "attendee_ids")}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="안건"
              value={form.agenda}
              onChange={(event) => setField("agenda", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="의결/조치 요약"
              value={form.decision_summary}
              onChange={(event) => setField("decision_summary", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {renderFileInput("minutes_pdf", "회의록 PDF")}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderFileInput("photo", "회의 사진", "image/*")}
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderTabContent = () => {
    const isEmbeddedSingle = embedded && filteredTabs.length === 1;
    const sectionDescriptionMap = {
      legalCerts: "설비·구역별 법정 검사와 인증의 유효기간, 결과, 증빙자료를 관리합니다.",
      committees: "위원회 구성원과 회의록을 함께 관리합니다.",
    };

    if (activeTab === "committees") {
      return (
        <Stack spacing={2}>
          <Paper className="safe-page-section safe-data-section"
            variant="outlined"
            sx={{ p: { xs: 1.25, md: 1.75 }, borderRadius: 1.5, bgcolor: "#fff" }}
          >
            <SectionHeader
              title="위원회 구성"
              description="위원회명, 역할 설명과 위원 명단을 관리합니다."
              icon={<GroupsIcon fontSize="small" />}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteRows("committees")}
                sx={{ fontWeight: 900 }}
              >
                삭제
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openDialog("committees")}
                sx={{ fontWeight: 900 }}
              >
                위원회 등록
              </Button>
            </SectionHeader>
            <CompactTable
              rows={data.committees}
              columns={columns.committees}
              idField="committee_id"
              selected={selected.committees}
              onSelect={(ids) => setSelected((prev) => ({ ...prev, committees: ids }))}
              onEdit={(row) => openDialog("committees", row)}
              emptyText="등록된 안전보건위원회가 없습니다."
            />
          </Paper>
          <Paper className="safe-page-section safe-data-section"
            variant="outlined"
            sx={{ p: { xs: 1.25, md: 1.75 }, borderRadius: 1.5, bgcolor: "#fff" }}
          >
            <SectionHeader
              title="회의록 / 회의 이력"
              description="회의 일시, 안건, 의결사항과 첨부파일을 기록합니다."
              icon={<FactCheckIcon fontSize="small" />}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteRows("committeeMeetings")}
                sx={{ fontWeight: 900 }}
              >
                삭제
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openDialog("committeeMeetings")}
                sx={{ fontWeight: 900 }}
              >
                회의록 등록
              </Button>
            </SectionHeader>
            <CompactTable
              rows={data.committeeMeetings}
              columns={columns.committeeMeetings}
              idField="meeting_id"
              selected={selected.committeeMeetings}
              onSelect={(ids) => setSelected((prev) => ({ ...prev, committeeMeetings: ids }))}
              onEdit={(row) => openDialog("committeeMeetings", row)}
              emptyText="등록된 회의록이 없습니다."
            />
          </Paper>
        </Stack>
      );
    }

    return (
      <Paper className="safe-page-section safe-data-section"
        variant="outlined"
        sx={{
          p: { xs: 1.25, md: isEmbeddedSingle ? 1.5 : 1.75 },
          borderRadius: 1.5,
          bgcolor: "#fff",
        }}
      >
        <SectionHeader
          title={activeMeta.label}
          description={sectionDescriptionMap[activeTab]}
          icon={activeMeta.icon}
          compact={isEmbeddedSingle}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => deleteRows(activeTab)}
            sx={{ fontWeight: 900 }}
          >
            삭제
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog(activeTab)}
            sx={{ fontWeight: 900 }}
          >
            등록
          </Button>
        </SectionHeader>
        <CompactTable
          rows={activeTab === "legalCerts" ? visibleLegalCerts : data[activeTab]}
          columns={columns[activeTab]}
          idField={idFields[activeTab]}
          selected={selected[activeTab]}
          onSelect={(ids) => setSelected((prev) => ({ ...prev, [activeTab]: ids }))}
          onEdit={(row) => openDialog(activeTab, row)}
          emptyText={activeMeta.emptyText}
          compact={isEmbeddedSingle}
        />
      </Paper>
    );
  };

  const dialogLabel = dialog.type === "committeeMeetings"
    ? "회의록"
    : tabs.find((tab) => tab.value === dialog.type)?.label || "항목";
  const dialogTitle = `${dialogLabel} ${dialog.row ? "수정" : "등록"}`;


  const contentBody = loading ? (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
      <CircularProgress />
    </Stack>
  ) : (
    renderTabContent()
  );

  const isCommitteeOnlyPage =
    filteredTabs.length === 2 &&
    filteredTabs.some((tab) => tab.value === "committees") &&
    filteredTabs.some((tab) => tab.value === "committeeMeetings");
  const shouldShowTabs = filteredTabs.length > 1 && !isCommitteeOnlyPage;

  return (
    <Box className={embedded ? undefined : "safe-page-root"}
      sx={{
        p: embedded ? 0 : { xs: 1.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      {!embedded ? (
      <SafePageHeader title={title || "안전보건 증빙 관리"}></SafePageHeader>
      ) : null}

      {!hideSummary && summaryCards.length ? (
      <Grid container spacing={1}>
        {summaryCards.map((card) => (
          <Grid key={`${card.key}-${card.filter || card.label}`} item xs={6} md={summaryGridMd}>
            <StatCard
              icon={card.icon}
              label={card.label}
              value={card.value}
              color={card.color}
              active={
                card.key === "legalCerts" && card.filter !== undefined
                  ? activeTab === "legalCerts" && legalCertStatusFilter === card.filter
                  : activeTab === card.key
              }
              onClick={
                card.key === "legalCerts" && card.filter !== undefined
                  ? () => {
                      setActiveTab("legalCerts");
                      setLegalCertStatusFilter(card.filter);
                      setSelected((prev) => ({ ...prev, legalCerts: [] }));
                    }
                  : shouldShowTabs && filteredTabs.some((tab) => tab.value === card.key)
                    ? () => setActiveTab(card.key)
                    : undefined
              }
            />
          </Grid>
        ))}
      </Grid>
      ) : null}

      {!shouldShowTabs ? (
        contentBody
      ) : (
      <Paper
        variant={embedded ? undefined : "outlined"}
        elevation={embedded ? 0 : undefined}
        sx={{
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: embedded ? "transparent" : undefined,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            minHeight: 48,
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 1,
            "& .MuiTab-root": {
              minHeight: 48,
              fontWeight: 900,
            },
          }}
        >
          {filteredTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
        <Box sx={{ p: { xs: 1.25, md: 2 }, bgcolor: "#fff" }}>
          {contentBody}
        </Box>
      </Paper>
      )}

      <Dialog
        open={dialog.open}
        onClose={saving ? undefined : closeDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            fontWeight: 900,
          }}
        >
          {dialogTitle}
          <IconButton onClick={closeDialog} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: "#f8fafc" }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1.5 }}>
            {renderDialogContent()}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            취소
          </Button>
          <Button variant="contained" onClick={submitDialog} disabled={saving}>
            {saving ? "저장 중" : "저장"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalComplianceManagement;

export const LegalCertManagement = () => (
  <LegalComplianceManagement
    visibleTabs={["legalCerts"]}
    title="설비·구역 검사·인증 관리"
    description="법정 검사와 인증 자료, 만료 상태와 증빙 파일을 관리합니다."
  />
);

export const SafetyCommitteeManagement = () => (
  <LegalComplianceManagement
    visibleTabs={["committees", "committeeMeetings"]}
    title="안전보건위원회"
    description="안전보건위원회 구성과 회의록, 참석자와 증빙자료를 관리합니다."
  />
);
